<template>
	<div
		class="offer__presentation"
		:class="{
			'offer__presentation--overprovider': isOverProviderTitle,
			'v-2': buyRentExpVariant === 'variant_2',
			'v-3': buyRentExpVariant === 'variant_3',
		}"
		v-if="promotionOffer"
	>
		<i18n path="WEBAPP_WATCH_OTHER_FREE_ON_PROVIDER" tag="p" v-if="isHighlighted" class="offer__presentation__info">
			<template #other>
				<span class="offer__presentation__info__bold text-high-active">{{ highlightedCTAText }}</span>
			</template>
			<template #provider>
				<span>{{ promotionOffer.package.clearName }}</span>
				<DisclaimerButton
					v-if="showDisclaimerButton"
					class="ml-1"
					:price="packagePlan?.retailPrice"
					:disclaimerText="disclaimerText"
				/>
			</template>
		</i18n>

		<p v-else class="offer__presentation__info">
			{{ subText }}
			<DisclaimerButton
				v-if="showDisclaimerButton"
				:price="packagePlan?.retailPrice"
				:disclaimerText="disclaimerText"
			/>
		</p>

		<!-- OVER_PROVIDER -->
		<BuyBoxOfferNewPresentation
			v-if="isOverProviderTitle && filterOption !== 'free'"
			:title="title"
			:offer="promotionOffer"
		/>
		<!-- OVER_PROVIDER -->
	</div>
</template>

<script lang="ts" setup>
import { ObjectType } from '@/@types/graphql-types';
import { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import BuyBoxOfferNewPresentation from '@/components/experiments/OverProviderExp/components/BuyBoxOfferNewPresentation.vue';
import { CatalogueItem } from '@/helpers/promotion-helper';
import { TitleDetail } from '@/interfaces/title-details-graphql';

import { computed, defineAsyncComponent } from 'vue';
import { PlanOffer } from '@/features/buybox/components/list/ListPromotionRow.vue';
import { useOverProviderExp } from '@/components/experiments/OverProviderExp';
import { getDisclaimerText } from '@/features/buybox/helpers/list-buybox-config';
import { useExperiment } from '@/libraries/experiment';

const DisclaimerButton = defineAsyncComponent(() => import('@/features/buybox/components/DisclaimerButton.vue'));

const props = defineProps<{
	title: TitleDetail;
	promotionOffer: TitleOfferFragment | null;
	packagePlan: PlanOffer | null;
	catalogueItem: CatalogueItem | null;
	inPromotionCatalogue?: boolean;
	isHighlighted?: boolean;
	// OVER_PROVIDER_EXP
	filterOption?: BuyBoxFilterOptions;
	// OVER_PROVIDER_EXP
}>();

const { t } = useI18n();

const { activeVariantWithControlGroup: buyRentExpVariant } = useExperiment('EXP_MERGE_BUY_RENT');

const { isOverProviderTitle } = useOverProviderExp({
	promotionOffer: () => props.promotionOffer,
});

const subText = computed(() => {
	if (!props.promotionOffer) return '';

	const provider = props.promotionOffer?.package.clearName;

	if (props.inPromotionCatalogue) {
		return t('WEBAPP_FREE_TRIAL_TEXT', { provider });
	}

	return props.title.objectType === ObjectType.Movie
		? t('WEBAPP_WATCH_SIMILAR_MOVIES_FREE_ON_X', { provider })
		: t('WEBAPP_WATCH_SIMILAR_TV_SHOWS_FREE_ON_X', { provider });
});

const highlightedCTAText = computed(() => {
	if (props.title.__typename === 'Show' || props.title.__typename === 'Season') return t('WEBAPP_OTHER_SHOWS');
	if (props.title.__typename === 'Movie') return t('WEBAPP_OTHER_MOVIES');
	return '';
});

const disclaimerText = computed(() => {
	if (!props.promotionOffer) return null;

	return getDisclaimerText(props.promotionOffer.package.shortName, props.promotionOffer.country);
});

const showDisclaimerButton = computed(() => {
	if (!!disclaimerText.value) return true;
	return props.promotionOffer?.package.packageId === 350 && props.packagePlan?.retailPrice;
});
</script>

<style lang="scss" scoped>
.offer__presentation {
	display: grid;
	width: 100%;

	// OVER_PROVIDER
	&--overprovider {
		gap: 8px;
		margin-bottom: -5px;
	}
	// OVER_PROVIDER

	/* Fallback for @container rule */
	@media (max-width: 750px) {
		justify-self: end;
		margin-top: 0;
		width: fit-content;
	}

	@container offer-container (max-width: 600px) {
		justify-self: end;
		margin-top: 0;
		width: fit-content;
	}

	&__info {
		margin: 0;
		margin-top: -2px;
		color: var(--jw-grey-200);
		font-size: 14px;
		line-height: 24px;
		max-height: 72px; /* Sibling flex items breaking overflow hiddenn without this */
		letter-spacing: 0.21px;
		display: -webkit-box;
		max-width: calc(50vw - 32px);
		line-clamp: 3;
		-webkit-line-clamp: 3;
		-webkit-box-orient: vertical;
		overflow: hidden;
		text-overflow: ellipsis;
		overflow-wrap: break-word;

		/* Fallback for @container rule */
		@media (max-width: 750px) {
			margin-top: 0;
		}

		@container offer-container (max-width: 600px) {
			margin-top: 0;
		}

		@media (max-width: 450px) {
			line-clamp: 4;
			-webkit-line-clamp: 4;
			max-height: none;
		}

		&__bold {
			font-weight: 900;
		}
	}

	/* Fallback for @container rule */
	@media (max-width: 750px) {
		align-items: start;
		margin-top: 0;
		text-align: end;
	}

	@container offer-container (max-width: 600px) {
		align-items: start;
		margin-top: 0;
		text-align: end;
	}

	&.v-2,
	&.v-3 {
		@media (max-width: 750px) {
			text-align: start;
			justify-self: start;
			max-width: 100%;

			.offer__presentation__info {
				max-width: 100%;
			}
		}

		@container offer-container (max-width: 600px) {
			justify-self: start;
			max-width: 100%;

			.offer__presentation__info {
				max-width: 100%;
			}
		}
	}

	&.v-2 {
		@media (max-width: 380px) {
			align-self: flex-end;
		}
	}
}
</style>
